import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import Tooltip from '@/components/Tooltip';
import { useAppSelector } from '@/store/hooks';
import { useI18nText } from '@/lib/utils/i18nText';
import { getPartnershipBadgeStyle } from '@/lib/constants/partnershipColors';
import { toGeneralPostType } from '@/lib/utils/channelPlatformMeta';
import { VIDEO_PREVIEW_COUNT } from '../../constants';
import type { CampaignPost } from '../../types';

/** Post-type → { tooltip label, icon color override } */
const POST_TYPE_META: Record<string, { label: string; color?: string }> = {
  youtube_video:        { label: 'YouTube video' },
  youtube_stream:       { label: 'YouTube livestream' },
  youtube_short:        { label: 'YouTube Short' },
  twitch_stream:        { label: 'Twitch live stream' },
  twitch_video:         { label: 'Twitch saved video/stream', color: '#a14340' },
  twitch_clip:          { label: 'Twitch clip',               color: '#62a341' },
  twitch_screenshot:    { label: 'Twitch screenshot',          color: '#495057' },
  tiktok_video:         { label: 'TikTok video' },
  douyin_video:         { label: 'Douyin video' },
  instagram_photo:      { label: 'Instagram photo' },
  instagram_video:      { label: 'Instagram video' },
  instagram_reel:       { label: 'Instagram Reel' },
  instagram_story:      { label: 'Instagram Story' },
  facebook_post:        { label: 'Facebook post' },
  facebook_video:       { label: 'Facebook video' },
  facebook_stream:      { label: 'Facebook livestream' },
  twitter_post:         { label: 'Twitter/X post' },
  bilibili_video:       { label: 'Bilibili video' },
  chzzk_stream:         { label: 'Chzzk live stream' },
  afreeca_stream:       { label: 'Soop live stream' },
};

const POP_W = 320;
const POP_H_EST = 400; // estimated max height for initial placement
const POP_GAP = 4;

/** Compute best fixed position for the advertising-type popover */
function calcPopoverPos(rect: DOMRect): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Prefer below; fall back to above
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const top = spaceBelow >= Math.min(POP_H_EST, 120) || spaceBelow >= spaceAbove
    ? rect.bottom + POP_GAP
    : rect.top - POP_H_EST - POP_GAP;

  // Prefer aligning to left edge of badge; shift left if it would overflow
  const rawLeft = rect.left;
  const left = rawLeft + POP_W > vw ? Math.max(0, vw - POP_W - 8) : rawLeft;

  return { top, left };
}

/** Badge that shows a legend popover of all advertising types on click (not
 * hover — a hover-triggered popover can get in the way while scanning a
 * table, so this only opens on an explicit click, with a tutorial-gated
 * tooltip explaining the affordance). */
export function PartnershipBadge({ label }: { label: string }) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes) as Array<{ _id?: string; id?: string; title?: string }>;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node) &&
          badgeRef.current && !badgeRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open]);

  // After popover renders, clamp top so it doesn't overflow below viewport
  useEffect(() => {
    if (!open || !popRef.current) return;
    const popRect = popRef.current.getBoundingClientRect();
    if (popRect.bottom > window.innerHeight - 8) {
      setPos((p) => p ? { ...p, top: p.top - (popRect.bottom - window.innerHeight + 8) } : p);
    }
  }, [open]);

  const handleClick = () => {
    if (open) { setOpen(false); return; }
    const rect = badgeRef.current?.getBoundingClientRect();
    if (rect) setPos(calcPopoverPos(rect));
    setOpen(true);
  };

  return (
    <>
      <Tooltip content={t('Click to see advertising type info', '클릭하면 광고 유형 안내를 볼 수 있습니다')}>
      <span
        ref={badgeRef}
        className="video-post-badge video-post-badge--type cursor-pointer"
        style={getPartnershipBadgeStyle(label)}
        onClick={handleClick}
      >
        {label}
      </span>
      </Tooltip>
      {open && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1400, width: POP_W }}
          className="bg-white border border-ggc-border-gray rounded-ggc-md shadow-ggc-lg py-1.5 px-2"
        >
          <p className="ggc-text-caption-s text-ggc-disabled uppercase tracking-wider px-1 mb-1">Advertising types</p>
          {advertisingTypes.map((type) => {
            const typeId = type._id ?? type.id ?? '';
            return (
              <div key={typeId} className="flex items-center gap-2 px-1 py-0.5 rounded-ggc-sm hover:bg-ggc-bg-base">
                <span
                  className="video-post-badge video-post-badge--type shrink-0"
                  style={getPartnershipBadgeStyle(typeId)}
                >
                  {typeId}
                </span>
                <span className="ggc-text-caption-s text-ggc-text-primary">{type.title}</span>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

export function VideoPostsList({ posts }: { posts: CampaignPost[] }) {
  const [showAll, setShowAll] = useState(false);
  const gameTrackingStreamTypes = new Set(['twitch_stream', 'chzzk_stream', 'afreeca_stream']);
  const noUrlStreamTypes = new Set(['twitch_stream', 'bilibili_stream', 'chzzk_stream', 'afreeca_stream']);
  const visible = showAll ? posts : posts.slice(0, VIDEO_PREVIEW_COUNT);
  const hidden = posts.length - VIDEO_PREVIEW_COUNT;
  return (
    <>
      {visible.map((post, pi) => {
        const postType = post.type ?? '';
        const chType = toGeneralPostType(postType);
        const isNoUrlStream = noUrlStreamTypes.has(postType);
        const streamedAt = post.campaignGameStreamedAt ?? post.publishedAt;
        const displayTitle = isNoUrlStream && streamedAt
          ? `Stream at ${streamedAt.replace('T', ' ').slice(0, 16)}`
          : post.title;
        const didntPlayGame = gameTrackingStreamTypes.has(postType) && post.stats?.didPlayCampaignGame === false;
        const typeBadge = postType.includes('vod')
          ? 'vod'
          : postType.includes('stream')
            ? 'stream'
            : postType.includes('short')
              ? 'short'
              : postType.includes('live')
                ? 'live'
                : null;
        const meta = POST_TYPE_META[postType];
        return (
          <div key={post.id ?? post._id ?? pi} className="video-post-card">
            <span className="video-post-icon">
              <Tooltip content={meta?.label ?? chType} placement="top">
                <span>
                  <ChannelTypeIcon
                    type={chType}
                    size={14}
                    color={meta?.color}
                  />
                </span>
              </Tooltip>
            </span>
            <div className="video-post-content">
              {/* Title + badge on same line — matches original layout */}
              <div className="video-post-title">
                <span className="video-post-title-inner">
                  {!isNoUrlStream && post.url
                    ? <a href={post.url} target="_blank" rel="noopener noreferrer" className="video-post-link">{displayTitle}</a>
                    : <span>{displayTitle}</span>
                  }
                </span>
                {/* Show partnership badge if set, else typeBadge — one badge per row */}
                {(post.partnership || typeBadge) && (
                  <PartnershipBadge label={post.partnership ?? typeBadge!} />
                )}
              </div>
              {/* Date on separate line below title */}
              {post.publishedAt && (
                <span className="video-post-date">{post.publishedAt.replace('T', ' ').slice(0, 16)}</span>
              )}
              {/* Warning at bottom */}
              {didntPlayGame && (
                <div className="video-post-warning">
                  <span className="video-post-warning-icon">ⓘ</span>
                  {"Didn't play campaign's game"}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!showAll && hidden > 0 && (
        <button className="toggle-more-btn" onClick={() => setShowAll(true)}>
          +{hidden}개 더보기
        </button>
      )}
      {showAll && posts.length > VIDEO_PREVIEW_COUNT && (
        <button className="toggle-more-btn" onClick={() => setShowAll(false)}>
          접기
        </button>
      )}
    </>
  );
}
